'use client';

import { useMemo } from 'react';

import { UserAvatarBubble } from '../../UserAvatarBubble';
import { type MessageBubbleProps } from '../types/chat';
import { ToolCallMessage } from './ToolCallMessage';

export function MessageBubble({ 
  message, 
  isStreaming = false, 
  onRetry, 
  onEdit, 
  onDelete,
  allMessages = [],
  currentUserName,
  currentUserEmail,
  currentUserAvatarUrl,
}: MessageBubbleProps) {
  const failed = Boolean((message.metadata as Record<string, unknown>)?.failed || (message.metadata as Record<string, unknown>)?.error);
  const formatToolResult = (result: string) => {
    try {
      const parsed: unknown = JSON.parse(result);
      return (
        <pre style={{ 
          fontSize: '12px', 
          color: 'var(--foreground-default)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          margin: 0,
          fontFamily: 'monospace'
        }}>
          {JSON.stringify(parsed, null, 2)}
        </pre>
      );
    } catch {
      return (
        <div style={{ 
          fontSize: '12px', 
          color: 'var(--foreground-default)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {result}
        </div>
      );
    }
  };

  const formatToolArguments = (args: unknown) => {
    try {
      return JSON.stringify(args, null, 2);
    } catch {
      return String(args);
    }
  };

  // Find screenshot from recent tool execution messages (for assistant messages)
  // Must be called before any early returns to satisfy rules of hooks
  const screenshotDataUrl = useMemo(() => {
    if (message.role !== 'assistant' || allMessages.length === 0) return null;
    
    // Look backwards from this message to find recent screenshot tool results
    const currentIndex = allMessages.findIndex(m => m.id === message.id);
    if (currentIndex === -1) return null;
    
    // Search backwards up to 10 messages for screenshot tool results
    for (let i = currentIndex - 1; i >= Math.max(0, currentIndex - 10); i--) {
      const priorMessage = allMessages[i];
      if (priorMessage.role === 'tool_execution' && priorMessage.toolCalls) {
        for (const toolCall of priorMessage.toolCalls) {
          if (toolCall.result) {
            try {
              const resultData = JSON.parse(toolCall.result);
              if (resultData.screenshot && typeof resultData.screenshot === 'string' && resultData.screenshot.startsWith('data:image/')) {
                return resultData.screenshot;
              }
            } catch {
              // Not JSON, skip
            }
          }
        }
      }
    }
    
    return null;
  }, [message.role, message.id, allMessages]);

  // Handle tool execution messages
  if (message.role === 'tool_execution') {
    return (
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        justifyContent: 'flex-start',
        flexDirection: 'row'
      }}>
        {/* Tool Icon */}
        <div style={{
          width: '28px',
          height: '28px',
          backgroundColor: 'var(--warning-default)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <span style={{ fontSize: '14px' }}>🔧</span>
        </div>

        {/* Tool Call Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ToolCallMessage 
            toolCalls={message.toolCalls || []} 
            content={message.content} 
          />
        </div>
      </div>
    );
  }

  const isUser = message.role === 'user';

  // Sanitize content to prevent base64 strings from breaking layout
  const sanitizeContent = (content: string | unknown): string => {
    if (typeof content !== 'string') {
      // Handle array content (multimodal)
      if (Array.isArray(content)) {
        return content
          .map(part => {
            if (typeof part === 'object' && part !== null) {
              if ('type' in part && part.type === 'text' && 'text' in part) {
                return sanitizeContent(part.text as string)
              }
              if ('type' in part && part.type === 'image_url') {
                return '[Image]'
              }
            }
            return String(part)
          })
          .join(' ')
      }
      return String(content)
    }
    
    // Replace base64 data URLs with placeholder (match any length)
    const base64Pattern = /data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]+/g
    return content.replace(base64Pattern, '[Screenshot Image]')
  }

  return (
    <div style={{ 
      display: 'flex', 
      gap: '12px', 
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      flexDirection: isUser ? 'row-reverse' : 'row',
      maxWidth: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Avatar */}
      <div style={{
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {isUser ? (
          <UserAvatarBubble
            name={currentUserName || currentUserEmail || 'User'}
            email={currentUserEmail}
            avatarUrl={currentUserAvatarUrl ?? null}
            size={28}
          />
        ) : (
          <div
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: 'var(--foreground-subtle)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: '14px' }}>🤖</span>
          </div>
        )}
      </div>

      {/* Message Content */}
      <div style={{ flex: 1, minWidth: 0, maxWidth: '100%', overflow: 'hidden', display: 'flex', justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start' }}>
        <div style={{
          padding: '12px 16px',
          backgroundColor: message.role === 'user' ? 'var(--primary-default)' : 'var(--background-secondary)',
          borderRadius: '12px',
          fontSize: '14px',
          lineHeight: '1.5',
          color: message.role === 'user' ? 'var(--primary-foreground)' : 'var(--foreground-default)',
          position: 'relative',
          maxWidth: 'min(90vw, 720px)',
          width: 'fit-content',
          minWidth: 0,
          wordBreak: 'break-all',
          overflowWrap: 'anywhere',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
          <div style={{
            wordBreak: 'break-all',
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
            maxWidth: '100%',
            overflow: 'hidden',
            display: 'block',
            backgroundColor: 'transparent'
          }}>
            {sanitizeContent(message.content)}
          </div>
          
          {/* Display screenshot if found */}
          {screenshotDataUrl && (
            <div style={{ marginTop: '12px', maxWidth: '100%', overflow: 'hidden' }}>
              <img 
                src={screenshotDataUrl} 
                alt="Screenshot" 
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  border: '1px solid var(--border-default)',
                  display: 'block',
                  boxSizing: 'border-box'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
          
          {/* Streaming indicator */}
          {isStreaming && message.role === 'assistant' && (
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              backgroundColor: 'var(--foreground-subtle)',
              borderRadius: '50%',
              marginLeft: '8px',
              animation: 'pulse 1.5s ease-in-out infinite'
            }} />
          )}

          {/* Message actions */}
          {message.role === 'user' && (onEdit || onDelete) && (
            <div style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              display: 'flex',
              gap: '4px',
              opacity: 0,
              transition: 'opacity 0.2s'
            }} className="message-actions">
              {onEdit && (
                <button
                  onClick={() => onEdit(message.content)}
                  style={{
                    padding: '2px 6px',
                    fontSize: '10px',
                    backgroundColor: 'var(--border-default)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
              )}
              {onDelete && (
                <button
                  onClick={onDelete}
                  style={{
                    padding: '2px 6px',
                    fontSize: '10px',
                    backgroundColor: 'var(--border-danger)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: 'var(--danger-default)'
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tool Calls */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            {message.toolCalls.map((toolCall, index) => (
              <div key={index} style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: 'var(--background-default)',
                border: '1px solid var(--border-default)',
                borderRadius: '8px',
                fontSize: '12px'
              }}>
                <div style={{
                  fontWeight: '600',
                  marginBottom: '4px',
                  fontSize: '12px',
                  fontFamily: 'inherit',
                  color: 'var(--foreground-default)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  🔧 {toolCall.name}
                  {toolCall.success === false && (
                    <span style={{ color: 'var(--danger-default)', fontSize: '10px' }}>
                      (Failed)
                    </span>
                  )}
                </div>
                
                <div style={{ 
                  marginBottom: '8px', 
                  color: 'var(--foreground-subtle)',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  backgroundColor: 'var(--background-secondary)',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  overflow: 'auto'
                }}>
                  {formatToolArguments(toolCall.arguments)}
                </div>
                
                {toolCall.result ? (
                  <div>
                    <div style={{
                      fontWeight: '600',
                      marginBottom: '4px',
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      color: toolCall.success ? 'var(--success-default)' : 'var(--danger-default)'
                    }}>
                      {toolCall.success ? '✅ Success' : '❌ Error'}
                    </div>
                    <div style={{
                      backgroundColor: 'var(--background-secondary)',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid var(--border-default)'
                    }}>
                      {formatToolResult(toolCall.result)}
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    color: 'var(--foreground-subtle)',
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    ⏳ Executing...
                    <div style={{
                      display: 'inline-flex',
                      gap: '2px'
                    }}>
                      <div style={{
                        width: '4px',
                        height: '4px',
                        backgroundColor: 'var(--foreground-subtle)',
                        borderRadius: '50%',
                        animation: 'bounce 1.4s ease-in-out infinite both'
                      }} />
                      <div style={{
                        width: '4px',
                        height: '4px',
                        backgroundColor: 'var(--foreground-subtle)',
                        borderRadius: '50%',
                        animation: 'bounce 1.4s ease-in-out 0.16s infinite both'
                      }} />
                      <div style={{
                        width: '4px',
                        height: '4px',
                        backgroundColor: 'var(--foreground-subtle)',
                        borderRadius: '50%',
                        animation: 'bounce 1.4s ease-in-out 0.32s infinite both'
                      }} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Files */}
        {message.files && message.files.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            {message.files.map((file, index) => (
              <div key={index} style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                backgroundColor: 'var(--info-background)',
                borderRadius: '6px',
                fontSize: '12px',
                color: 'var(--info-default)',
                marginRight: '4px',
                marginBottom: '4px'
              }}>
                📎 {file.name}
                <span style={{ color: 'var(--foreground-subtle)' }}>
                  ({(file.size / 1024).toFixed(1)}KB)
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Retry only when message failed (per style guide) */}
        {message.role === 'assistant' && failed && onRetry && (
          <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              color: 'var(--danger-default)',
              backgroundColor: 'var(--danger-background)',
              border: '1px solid var(--border-danger)',
              borderRadius: '9999px',
              padding: '2px 8px'
            }}>
              Error
            </span>
            <button
              onClick={onRetry}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                margin: 0,
                color: 'var(--primary-default)',
                fontSize: '12px',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}
      </div>

      <style>{`
        .message-actions {
          opacity: 0;
        }
        
        .message-actions:hover {
          opacity: 1;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

